import { Color } from './java/Color';
import { Font, FontMetrics, Graphics2D } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { Random } from './java/Random';

import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { Game } from './Game';
import { CoinShop } from './CoinShop';
import { Perks } from './Perks';
import { GamePalette } from './GamePalette';
import { TrailPool } from './TrailPool';
import { PlayerSkins } from './PlayerSkins';
import { Settings } from './Settings';
import { HUD } from './HUD';
import { PlayerProjectile } from './PlayerProjectile';
import { EnemyBossBullet } from './EnemyBossBullet';

export class Player extends GameObject {

  private static readonly SIZE = 48;
  private static readonly R = 12;
  private static readonly FILL = new Color(230, 234, 240);
  private static readonly WHITE = new Color(255, 255, 255);
  private static readonly TRAIL_BASE = new Color(200, 210, 220);

  private static readonly ACCEL = 0.45;
  private static readonly DECEL = 0.30;

  private static readonly MULT_FONT = new Font('Arial', Font.BOLD, 16);

  private handler: Handler;
  private trailTick = 0;
  private readonly boundsRect: Rectangle = new Rectangle();

  // Input state
  public moveUp = false;
  public moveDown = false;
  public moveLeft = false;
  public moveRight = false;

  // Invincibility frames
  private iFrames = 0;
  private static readonly I_FRAME_DURATION = 45; // ~0.75 seconds

  // Streak system
  private streakTicks = 0;
  private streakLevel = 0;
  private hitPop = 0;
  private auraPhase = 0;

  private static readonly STREAK_T1 = 180;
  private static readonly STREAK_T2 = 480;
  private static readonly STREAK_T3 = 900;
  private static readonly STREAK_MAX = 1200;

  // ===== DASH =====
  public dashInput = false;
  private dashing = false;
  private dashTimer = 0;
  private dashVelX = 0;
  private dashVelY = 0;
  private dashCooldown = 0;
  private static readonly DASH_DURATION = 8;    // ticks (~0.13s)
  private static readonly DASH_COOLDOWN = 90;   // ticks (~1.5s)
  private static readonly DASH_DISTANCE = 120;
  private static readonly DASH_SPEED = Player.DASH_DISTANCE / Player.DASH_DURATION;

  // ===== SHIELD =====
  private shieldCharges: number = 1 + CoinShop.getExtraShieldCharges();
  private shieldActive = true;
  private shieldCooldown = 0;
  private shieldBreakEffect = 0; // 1.0 on break, decays
  private shieldPulse = 0;
  private static readonly SHIELD_COOLDOWN = 1800; // 30 seconds
  // Shatter particles [x, y, vx, vy, life]
  private shieldParticles: number[][] | null = null;

  // ===== SLOW-MOTION =====
  public slowmoInput = false;
  private slowmoMaxCharges: number = 1 + Perks.getExtraSlowmoCharges() + CoinShop.getExtraSlowmoCharges();
  private slowmoCharges: number = this.slowmoMaxCharges;
  private slowmoTimer = 0;
  private slowmoRegenTimer = 0;
  private static readonly SLOWMO_DURATION = 150; // ticks at full speed (~2.5s)
  private static readonly SLOWMO_REGEN_TICKS = 600; // ticks to regen one charge (~10s)

  // ===== COMBAT: SHOOTING =====
  public shootInput = false;
  private ammo = 5;
  private maxAmmo = 5;
  private ammoRegenTimer = 0;
  private shootCooldown = 0;
  private facingX = 1;
  private facingY = 0;
  private static readonly AMMO_REGEN_TICKS = 90; // 1.5 seconds per ammo
  private static readonly SHOOT_COOLDOWN = 8;
  private static readonly PROJECTILE_SPEED = 12;
  private static readonly PROJECTILE_DAMAGE = 30;
  private static readonly DASH_STRIKE_DAMAGE = 40;

  // ===== COMBAT: PARRY =====
  public parryInput = false;
  private parrying = false;
  private parryTimer = 0;
  private parryCooldown = 0;
  private parryFlash = 0;
  private static readonly PARRY_WINDOW = 15;   // ~0.25s
  private static readonly PARRY_COOLDOWN = 60;  // ~1s
  private static readonly PARRY_RADIUS = 80;

  constructor(x: number, y: number, id: ID, handler: Handler) {
    super(x, y, id);
    this.handler = handler;
  }

  public getStreakLevel(): number { return this.streakLevel; }
  public getStreakTicks(): number { return this.streakTicks; }

  public getMultiplier(): number {
    if (this.streakTicks >= Player.STREAK_T3) return 3;
    if (this.streakTicks >= Player.STREAK_T2) return 2;
    return 1;
  }

  // Ability getters for HUD
  public getDashCooldownPct(): number {
    return this.dashCooldown / (Player.DASH_COOLDOWN * Perks.getDashCooldownMultiplier());
  }
  public isDashing(): boolean { return this.dashing; }
  public isShieldActive(): boolean { return this.shieldActive; }
  public getShieldCooldownPct(): number {
    return this.shieldCooldown / (Player.SHIELD_COOLDOWN * Perks.getShieldCooldownMultiplier());
  }
  public getShieldBreakEffect(): number { return this.shieldBreakEffect; }
  public getSlowmoCharges(): number { return this.slowmoCharges; }
  public getSlowmoMaxCharges(): number { return this.slowmoMaxCharges; }
  public isSlowmoActive(): boolean { return this.slowmoTimer > 0; }
  public getSlowmoTimerPct(): number { return this.slowmoTimer / Player.SLOWMO_DURATION; }
  public getSlowmoRegenPct(): number { return this.slowmoRegenTimer / Player.SLOWMO_REGEN_TICKS; }

  // Combat getters for HUD
  public getAmmo(): number { return this.ammo; }
  public getMaxAmmo(): number { return this.maxAmmo; }
  public getShootCooldownPct(): number { return this.shootCooldown / Player.SHOOT_COOLDOWN; }
  public getParryCooldownPct(): number { return this.parryCooldown / Player.PARRY_COOLDOWN; }
  public isParrying(): boolean { return this.parrying; }
  public getParryFlash(): number { return this.parryFlash; }

  public addShieldCharge(): void {
    this.shieldActive = true;
    this.shieldCooldown = 0;
    this.shieldCharges = 1 + CoinShop.getExtraShieldCharges();
  }

  public addSlowmoCharge(): void { this.slowmoCharges++; }

  public getBounds(): Rectangle {
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = Player.SIZE;
    this.boundsRect.height = Player.SIZE;
    return this.boundsRect;
  }

  public tick(): void {
    // Dash cooldown always ticks (not affected by slow-mo from player perspective)
    if (this.dashCooldown > 0) this.dashCooldown--;
    if (this.shieldCooldown > 0) {
      this.shieldCooldown--;
      if (this.shieldCooldown <= 0) {
        this.shieldActive = true;
        this.shieldCharges = 1 + CoinShop.getExtraShieldCharges();
      }
    }
    if (this.shieldBreakEffect > 0.01) this.shieldBreakEffect *= 0.92;
    else this.shieldBreakEffect = 0;
    this.shieldPulse += 0.05;

    // Slow-motion timer
    if (this.slowmoTimer > 0) {
      this.slowmoTimer--;
      if (this.slowmoTimer <= 0) {
        Game.setTimeScale(1);
      }
    }

    // Slow-motion charge regen
    if (this.slowmoTimer <= 0 && this.slowmoCharges < this.slowmoMaxCharges) {
      this.slowmoRegenTimer++;
      if (this.slowmoRegenTimer >= Player.SLOWMO_REGEN_TICKS) {
        this.slowmoCharges++;
        this.slowmoRegenTimer = 0;
      }
    }

    // Update shield particles
    if (this.shieldParticles !== null) {
      let anyAlive = false;
      for (const p of this.shieldParticles) {
        if (p[4] > 0) {
          p[0] += p[2];
          p[1] += p[3];
          p[2] *= 0.96;
          p[3] *= 0.96;
          p[4] -= 0.02;
          anyAlive = true;
        }
      }
      if (!anyAlive) this.shieldParticles = null;
    }

    // === COMBAT COOLDOWNS ===
    if (Game.combatMode) {
      if (this.shootCooldown > 0) this.shootCooldown--;
      if (this.parryCooldown > 0) this.parryCooldown--;
      if (this.parryTimer > 0) {
        this.parryTimer--;
        if (this.parryTimer <= 0) this.parrying = false;
      }
      if (this.parryFlash > 0.01) this.parryFlash *= 0.88; else this.parryFlash = 0;

      // Ammo regen — faster at higher streaks
      if (this.ammo < this.maxAmmo) {
        this.ammoRegenTimer++;
        const regenSpeed = Math.trunc(Player.AMMO_REGEN_TICKS / Math.max(1, this.getMultiplier()));
        if (this.ammoRegenTimer >= regenSpeed) {
          this.ammo++;
          this.ammoRegenTimer = 0;
        }
      }

      // Parry check (active window)
      if (this.parrying) this.checkParry();
    }

    // === DASH ===
    if (this.dashInput && !this.dashing && this.dashCooldown <= 0) {
      this.dashInput = false;
      this.startDash();
    } else {
      this.dashInput = false;
    }

    if (this.dashing) {
      this.x += this.dashVelX;
      this.y += this.dashVelY;
      this.x = Game.clamp(this.x, 0, Game.WIDTH - 37);
      this.y = Game.clamp(this.y, 0, Game.HEIGHT - 60);
      this.dashTimer--;

      // Afterimage trail — intense during dash
      const dashCol = GamePalette.accent();
      TrailPool.add(this.x, this.y, dashCol, Player.SIZE, Player.SIZE, 0.06, PlayerSkins.getSelectedTrailShape());

      // Dash-strike: damage enemies while dashing (combat mode)
      if (Game.combatMode) {
        this.checkDashStrike();
      }

      if (this.dashTimer <= 0) {
        this.dashing = false;
        this.dashCooldown = Math.trunc(Player.DASH_COOLDOWN * Perks.getDashCooldownMultiplier());
        this.velX = this.dashVelX * 0.3; // carry some momentum
        this.velY = this.dashVelY * 0.3;
      }
      // Skip normal movement during dash, but still check streak and collision
      this.streakTicks++;
      const targetStreakDash = Math.min(this.streakTicks / Player.STREAK_MAX, 1);
      this.streakLevel += (targetStreakDash - this.streakLevel) * 0.04;
      this.auraPhase += 0.06 + this.streakLevel * 0.04;
      if (this.hitPop > 0.01) this.hitPop *= 0.9; else this.hitPop = 0;
      if (this.iFrames > 0) this.iFrames--;
      // No collision during dash — i-frames
      return;
    }

    // === SLOW-MO ===
    if (this.slowmoInput && this.slowmoCharges > 0 && this.slowmoTimer <= 0) {
      this.slowmoInput = false;
      this.slowmoCharges--;
      this.slowmoRegenTimer = 0;
      this.slowmoTimer = Player.SLOWMO_DURATION;
      Game.setTimeScale(0.3);
    } else {
      this.slowmoInput = false;
    }

    // === COMBAT: SHOOT (mouse hold or F key) ===
    if (Game.combatMode && (Game.mouseShootHeld || this.shootInput)) {
      this.shootInput = false;
      this.shoot();
    } else {
      this.shootInput = false;
    }

    // === COMBAT: PARRY ===
    if (Game.combatMode && this.parryInput && !this.parrying && this.parryCooldown <= 0) {
      this.parryInput = false;
      this.activateParry();
    } else {
      this.parryInput = false;
    }

    // Normal movement
    const speed = this.handler.spd;

    let targetVX = 0;
    let targetVY = 0;
    if (this.moveUp) targetVY -= speed;
    if (this.moveDown) targetVY += speed;
    if (this.moveLeft) targetVX -= speed;
    if (this.moveRight) targetVX += speed;

    if (targetVX !== 0 && targetVY !== 0) {
      targetVX *= 0.707;
      targetVY *= 0.707;
    }

    // Update facing direction for shooting
    if (targetVX !== 0 || targetVY !== 0) {
      const len = Math.sqrt(targetVX * targetVX + targetVY * targetVY);
      this.facingX = targetVX / len;
      this.facingY = targetVY / len;
    }

    const lerpX = targetVX !== 0 ? Player.ACCEL : Player.DECEL;
    const lerpY = targetVY !== 0 ? Player.ACCEL : Player.DECEL;
    this.velX += (targetVX - this.velX) * lerpX;
    this.velY += (targetVY - this.velY) * lerpY;

    if (Math.abs(this.velX) < 0.1 && targetVX === 0) this.velX = 0;
    if (Math.abs(this.velY) < 0.1 && targetVY === 0) this.velY = 0;

    this.x += this.velX;
    this.y += this.velY;
    this.x = Game.clamp(this.x, 0, Game.WIDTH - 37);
    this.y = Game.clamp(this.y, 0, Game.HEIGHT - 60);

    // Streak grows
    this.streakTicks++;
    const targetStreak = Math.min(this.streakTicks / Player.STREAK_MAX, 1);
    this.streakLevel += (targetStreak - this.streakLevel) * 0.04 * Perks.getStreakSpeedMultiplier();
    this.auraPhase += 0.06 + this.streakLevel * 0.04;

    if (this.hitPop > 0.01) this.hitPop *= 0.9;
    else this.hitPop = 0;

    // Trail
    if (Settings.getPlayerTrail()) {
      const trailRate = this.streakLevel > 0.5 ? 2 : 3;
      const trailLife = 0.045 - this.streakLevel * 0.02;
      const trailCol = this.lerpColor(Player.TRAIL_BASE, GamePalette.accent(), this.streakLevel * 0.6);

      if (++this.trailTick % trailRate === 0) {
        TrailPool.add(this.x, this.y, trailCol, Player.SIZE, Player.SIZE,
          Math.max(trailLife, 0.015), PlayerSkins.getSelectedTrailShape());
      }
    }

    if (this.iFrames > 0) this.iFrames--;
    this.collision();
  }

  private startDash(): void {
    this.dashing = true;
    this.dashTimer = Player.DASH_DURATION;
    this.iFrames = Player.DASH_DURATION + 5; // i-frames during dash + tiny buffer

    // Dash in movement direction, or facing direction if stationary
    let dx = 0;
    let dy = 0;
    if (this.moveUp) dy -= 1;
    if (this.moveDown) dy += 1;
    if (this.moveLeft) dx -= 1;
    if (this.moveRight) dx += 1;

    // If no direction held, use current velocity
    if (dx === 0 && dy === 0) {
      dx = this.velX;
      dy = this.velY;
    }

    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 0) {
      this.dashVelX = (dx / len) * Player.DASH_SPEED;
      this.dashVelY = (dy / len) * Player.DASH_SPEED;
    } else {
      // Default: dash right
      this.dashVelX = Player.DASH_SPEED;
      this.dashVelY = 0;
    }

    Game.triggerScreenShake(3);
  }

  // === COMBAT: SHOOTING ===
  private shoot(): void {
    if (this.ammo <= 0 || this.shootCooldown > 0) return;
    this.ammo--;
    this.shootCooldown = Player.SHOOT_COOLDOWN;
    this.ammoRegenTimer = 0;

    // Aim at mouse cursor
    const cx = this.x + Math.trunc(Player.SIZE / 2);
    const cy = this.y + Math.trunc(Player.SIZE / 2);
    let dx = Game.mouseGameX - cx;
    let dy = Game.mouseGameY - cy;
    let len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) { dx = this.facingX; dy = this.facingY; len = 1; }
    dx /= len;
    dy /= len;

    const bx = cx - 5;
    const by = cy - 5;
    this.handler.addObject(new PlayerProjectile(bx, by, ID.PlayerProjectile, this.handler,
      dx * Player.PROJECTILE_SPEED, dy * Player.PROJECTILE_SPEED, Player.PROJECTILE_DAMAGE));
    Game.triggerScreenShake(1.5);
  }

  // === COMBAT: PARRY ===
  private activateParry(): void {
    this.parrying = true;
    this.parryTimer = Player.PARRY_WINDOW;
    this.parryCooldown = Player.PARRY_COOLDOWN;
  }

  private checkParry(): void {
    const cx = this.x + Math.trunc(Player.SIZE / 2);
    const cy = this.y + Math.trunc(Player.SIZE / 2);

    const objects: GameObject[] = this.handler.getObjects();
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      if (!(obj instanceof EnemyBossBullet)) continue;
      const bullet: EnemyBossBullet = obj;
      if (bullet.isParried()) continue;

      const bx = obj.getX() + 8;
      const by = obj.getY() + 8;
      const dist = Math.sqrt((bx - cx) * (bx - cx) + (by - cy) * (by - cy));
      if (dist < Player.PARRY_RADIUS) {
        bullet.parry();
        this.parryFlash = 1;
        this.parrying = false;
        this.parryTimer = 0;
        Game.triggerScreenShake(6);
        this.streakTicks += 300; // Big streak boost for successful parry
        break;
      }
    }
  }

  // === COMBAT: DASH-STRIKE ===
  private checkDashStrike(): void {
    const objects: GameObject[] = this.handler.getObjects();
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      const oid = obj.getId();
      if (oid === ID.BasicEnemy || oid === ID.FastEnemy
        || oid === ID.SmartEnemy || oid === ID.HardEnemy
        || oid === ID.EnemyBoss) {
        if (obj instanceof EnemyBossBullet) continue;
        if (this.getBounds().intersects(obj.getBounds())) {
          obj.takeDamage(Player.DASH_STRIKE_DAMAGE);
        }
      }
    }
  }

  private getDamage(id: ID): number {
    switch (id) {
      case ID.FastEnemy:    return 8;
      case ID.BasicEnemy:   return 12;
      case ID.HardEnemy:    return 15;
      case ID.SmartEnemy:   return 20;
      case ID.EnemyBoss:    return 25;
      default:              return 12;
    }
  }

  private collision(): void {
    if (this.iFrames > 0) return;

    const objects: GameObject[] = this.handler.getObjects();
    for (let i = 0; i < objects.length; i++) {
      const tempObject = objects[i];
      const id = tempObject.getId();
      if (id === ID.BasicEnemy || id === ID.FastEnemy
        || id === ID.SmartEnemy || id === ID.HardEnemy
        || id === ID.EnemyBoss) {
        // Don't collide with parried boss bullets
        if (tempObject instanceof EnemyBossBullet && tempObject.isParried()) continue;

        if (this.getBounds().intersects(tempObject.getBounds())) {
          // Shield absorbs the hit
          if (this.shieldActive) {
            this.shieldCharges--;
            if (this.shieldCharges <= 0) {
              this.shieldActive = false;
              this.shieldCooldown = Math.trunc(Player.SHIELD_COOLDOWN * Perks.getShieldCooldownMultiplier());
            }
            this.shieldBreakEffect = 1;
            this.spawnShieldParticles();
            this.iFrames = Player.I_FRAME_DURATION;
            Game.triggerScreenShake(5);
            return;
          }

          const dmg = this.getDamage(id) * Perks.getDamageMultiplier();
          HUD.HEALTH -= dmg;
          Game.addDamage(dmg);
          this.iFrames = Player.I_FRAME_DURATION;
          Game.triggerHit();

          if (this.streakTicks > Player.STREAK_T1) {
            this.hitPop = 1;
          }
          this.streakTicks = 0;
          this.streakLevel = 0;
          return;
        }
      }
    }
  }

  private spawnShieldParticles(): void {
    this.shieldParticles = Array.from({ length: 24 }, () => new Array<number>(5).fill(0));
    const rng = new Random();
    const cx = Math.trunc(this.x) + Math.trunc(Player.SIZE / 2);
    const cy = Math.trunc(this.y) + Math.trunc(Player.SIZE / 2);
    for (let i = 0; i < 24; i++) {
      const angle = rng.nextFloat() * (Math.PI * 2);
      const speed = 2 + rng.nextFloat() * 5;
      this.shieldParticles[i] = [
        cx + Math.cos(angle) * (Math.trunc(Player.SIZE / 2) + 8),
        cy + Math.sin(angle) * (Math.trunc(Player.SIZE / 2) + 8),
        Math.cos(angle) * speed,
        Math.sin(angle) * speed,
        0.8 + rng.nextFloat() * 0.2,
      ];
    }
  }

  public render(g: Graphics2D): void {
    const g2: Graphics2D = g;
    const ix = Math.trunc(this.x);
    const iy = Math.trunc(this.y);
    const cx = ix + Math.trunc(Player.SIZE / 2);
    const cy = iy + Math.trunc(Player.SIZE / 2);

    const accent = GamePalette.accent();

    // Shield shatter particles
    if (this.shieldParticles !== null) {
      for (const p of this.shieldParticles) {
        if (p[4] > 0) {
          const alpha = Math.trunc(p[4] * 200);
          g2.setColor(new Color(140, 220, 255, Math.min(alpha, 255)));
          const sz = 3 + Math.trunc(p[4] * 4);
          g2.fillRect(Math.trunc(p[0]) - Math.trunc(sz / 2), Math.trunc(p[1]) - Math.trunc(sz / 2), sz, sz);
        }
      }
    }

    // Parry flash — expanding teal ring on successful parry
    if (this.parryFlash > 0.05) {
      const parryR = Math.trunc((1 - this.parryFlash) * 100) + Math.trunc(Player.SIZE / 2);
      const parryA = Math.trunc(this.parryFlash * 200);
      g2.setColor(new Color(78, 205, 196, Math.min(parryA, 255)));
      g2.drawOval(cx - parryR, cy - parryR, parryR * 2, parryR * 2);
    }

    // Parry active window — pulsing circle
    if (this.parrying && this.parryTimer > 0) {
      const pulse = Math.sin(this.parryTimer * 0.8) * 0.3 + 0.7;
      const pRadius = Math.trunc(Player.PARRY_RADIUS * pulse);
      g2.setColor(new Color(78, 205, 196, 30));
      g2.fillOval(cx - pRadius, cy - pRadius, pRadius * 2, pRadius * 2);
      g2.setColor(new Color(78, 205, 196, 80));
      g2.drawOval(cx - pRadius, cy - pRadius, pRadius * 2, pRadius * 2);
    }

    // Hit pop — expanding white ring on streak break
    if (this.hitPop > 0) {
      const popRadius = Math.trunc((1 - this.hitPop) * 80);
      const popAlpha = Math.trunc(this.hitPop * 200);
      g2.setColor(new Color(255, 255, 255, Math.min(popAlpha, 255)));
      g2.drawOval(cx - popRadius, cy - popRadius, popRadius * 2, popRadius * 2);
    }

    // Shield break flash
    if (this.shieldBreakEffect > 0.05) {
      const flashR = Math.trunc((1 - this.shieldBreakEffect) * 60) + Math.trunc(Player.SIZE / 2);
      const flashAlpha = Math.trunc(this.shieldBreakEffect * 180);
      g2.setColor(new Color(140, 220, 255, Math.min(flashAlpha, 255)));
      g2.drawOval(cx - flashR, cy - flashR, flashR * 2, flashR * 2);
    }

    // Particle aura — orbiting dots at high streak
    if (this.streakLevel > 0.3) {
      const auraIntensity = (this.streakLevel - 0.3) / 0.7;
      const count = 3 + Math.trunc(auraIntensity * 5);
      const auraAlpha = Math.trunc(auraIntensity * 80);
      const auraColor = new Color(accent.getRed(), accent.getGreen(), accent.getBlue(),
        Math.min(auraAlpha, 255));
      for (let i = 0; i < count; i++) {
        const a = this.auraPhase + i * (Math.PI * 2 / count);
        const dist = 30 + Math.sin(this.auraPhase * 1.5 + i) * 8;
        const px = cx + Math.trunc(Math.cos(a) * dist);
        const py = cy + Math.trunc(Math.sin(a) * dist);
        const dotSize = 2 + Math.trunc(auraIntensity * 3);
        g2.setColor(auraColor);
        g2.fillOval(px - Math.trunc(dotSize / 2), py - Math.trunc(dotSize / 2), dotSize, dotSize);
      }
    }

    // I-frame blink
    let visible = true;
    let iFrameAlpha = 1;
    if (this.iFrames > 0 && !this.dashing) {
      visible = Math.trunc(this.iFrames / 3) % 2 === 0;
      iFrameAlpha = 0.5 + Math.sin(this.iFrames * 0.5) * 0.3;
    }

    // Shield bubble (drawn before player)
    if (this.shieldActive && visible) {
      const sPulse = Math.sin(this.shieldPulse) * 0.5 + 0.5;
      const shieldR = Math.trunc(Player.SIZE / 2) + 10 + Math.trunc(sPulse * 3);
      const sAlpha = 25 + Math.trunc(sPulse * 20);
      g2.setColor(new Color(140, 220, 255, sAlpha));
      g2.fillOval(cx - shieldR, cy - shieldR, shieldR * 2, shieldR * 2);
      g2.setColor(new Color(140, 220, 255, 50 + Math.trunc(sPulse * 30)));
      g2.drawOval(cx - shieldR, cy - shieldR, shieldR * 2, shieldR * 2);
      // Inner ring
      const innerR = shieldR - 4;
      g2.setColor(new Color(200, 240, 255, 20 + Math.trunc(sPulse * 15)));
      g2.drawOval(cx - innerR, cy - innerR, innerR * 2, innerR * 2);
    }

    if (visible) {
      const skinShape = PlayerSkins.getSelectedShape();
      const skinFill = PlayerSkins.getSelectedFill();
      const skinGlow = PlayerSkins.getSelectedGlow();

      // Dash — bright afterglow during dash
      if (this.dashing) {
        g2.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), 60));
        PlayerSkins.drawShape(g2, skinShape, ix - 8, iy - 8, Player.SIZE + 16, Player.R + 8,
          new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), 60));
      }

      // Outer glow
      const glowAlpha = Math.trunc((30 + Math.trunc(this.streakLevel * 60)) * iFrameAlpha);
      const glowSize = 5 + Math.trunc(this.streakLevel * 6);
      const glowCol = this.lerpColor(skinGlow, accent, this.streakLevel * 0.5);
      PlayerSkins.drawShape(g2, skinShape, ix - glowSize, iy - glowSize,
        Player.SIZE + glowSize * 2, Player.R + glowSize,
        new Color(glowCol.getRed(), glowCol.getGreen(), glowCol.getBlue(),
          Math.min(glowAlpha, 255)));

      // Inner glow
      const innerAlpha = Math.trunc((60 + Math.trunc(this.streakLevel * 50)) * iFrameAlpha);
      PlayerSkins.drawShape(g2, skinShape, ix - 2, iy - 2, Player.SIZE + 4, Player.R + 2,
        new Color(glowCol.getRed(), glowCol.getGreen(), glowCol.getBlue(),
          Math.min(innerAlpha, 255)));

      // Main shape
      let fill: Color;
      if (this.dashing) {
        fill = this.lerpColor(skinFill, accent, 0.6);
      } else if (this.iFrames > 0) {
        fill = this.lerpColor(skinFill, Player.WHITE, Math.abs(Math.sin(this.iFrames * 0.4)) * 0.5);
      } else {
        fill = this.lerpColor(skinFill, accent, this.streakLevel * 0.25);
      }
      PlayerSkins.drawShape(g2, skinShape, ix, iy, Player.SIZE, Player.R, fill);
    }

    // Multiplier text above player
    if (this.getMultiplier() > 1) {
      g2.setFont(Player.MULT_FONT);
      g2.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(),
        160 + Math.trunc(this.streakLevel * 95)));
      const mult = 'x' + this.getMultiplier();
      const fm: FontMetrics = g2.getFontMetrics();
      g2.drawString(mult, cx - Math.trunc(fm.stringWidth(mult) / 2), iy - 12);
    }
  }

  private lerpColor(a: Color, b: Color, t: number): Color {
    t = Math.max(0, Math.min(1, t));
    return new Color(
      Math.trunc(a.getRed() + (b.getRed() - a.getRed()) * t),
      Math.trunc(a.getGreen() + (b.getGreen() - a.getGreen()) * t),
      Math.trunc(a.getBlue() + (b.getBlue() - a.getBlue()) * t));
  }
}
